'use client';
import { Virtuoso, VirtuosoGrid } from 'react-virtuoso';

import Container from '@/components/@base/container';
import Props from '@/components/app/archive/components/products/infinite-list/type';
import Loading from '@/components/app/archive/components/products/loading';
import RowProductCard from '@/components/app/archive/components/products/row-product-card';
import { FILTER_KEYS } from '@/components/app/archive/helpers';
import { VIEW_MODE } from '@/components/app/archive/helpers/hooks/type';
import { useGetInfiniteProductList } from '@/components/app/archive/services/hooks';
import { PRODUCT_TYPE } from '@/components/app/product/components/@shared/type';
import { ProductCard } from '@/components/shared/components/cards';
import Demo from '@/components/shared/components/cards/demo';
import ReadySiteCard from '@/components/shared/components/cards/readySite';
import PaginationButtons from '@/components/shared/components/pagination-buttons';

import {
  DATA_CY_LOADING,
  DATA_CY_PRODUCT_CARD,
  DATA_CY_PRODUCT_LIST_CARD,
} from '@/constant/cypress';
import ProductModel from '@/models/product.model';

const PAGINATION_OFFSET = 4;

export default function InfiniteProductList({
  filters = {},
  viewMode,
  totalPages = 1,
  onPageChange,
  pagePath,
}: Props) {
  const {
    data,
    error: infiniteQueryError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isInfiniteLoading,
    isPending,
  } = useGetInfiniteProductList({ filters, enabled: true, initialPage: 2 }); //start the infinite query from page 2 because the first page is already fetched server-side

  const infiniteProducts =
    data?.pages.reduce((acc: ProductModel[], page) => {
      return [...acc, ...page.data];
    }, []) ?? [];
  const currentPage = data?.pages[data?.pages.length - 1].currentPage || 1;

  const handleEndReached = () => {
    if (currentPage) {
      if (
        hasNextPage &&
        !isFetchingNextPage &&
        !isPending &&
        currentPage < PAGINATION_OFFSET
      ) {
        fetchNextPage();
      }
    }
  };

  const InfinityScrollLoading = () =>
    (isFetchingNextPage || isInfiniteLoading) && (
      <Container data-cy={DATA_CY_LOADING} className='py-4'>
        <Loading />
      </Container>
    );

  const renderProducts = (products: ProductModel[]) => {
    if (filters[FILTER_KEYS.VIEW_DEMO] === true) {
      return (
        <>
          <VirtuosoGrid
            useWindowScroll
            endReached={handleEndReached}
            overscan={200}
            data={products}
            listClassName='mx-4 grid grid-cols-1 gap-4 md:m-0 md:grid-cols-2 lg:grid-cols-3'
            itemContent={(index, product) => <Demo data={product} />}
          />
          <InfinityScrollLoading />
        </>
      );
    }

    if (viewMode === VIEW_MODE.LIST) {
      return (
        <Container className='hidden md:block'>
          <Virtuoso
            useWindowScroll
            endReached={handleEndReached}
            overscan={200}
            data={products}
            components={{
              Footer: InfinityScrollLoading,
            }}
            itemContent={(index, product) => (
              <Container className='py-2'>
                <RowProductCard data-cy={DATA_CY_PRODUCT_LIST_CARD} {...product} />
              </Container>
            )}
          />
        </Container>
      );
    }

    return (
      <>
        <Container className='block md:hidden'>
          <Virtuoso
            useWindowScroll
            endReached={handleEndReached}
            overscan={200}
            data={products}
            components={{
              Footer: InfinityScrollLoading,
            }}
            itemContent={(index, product) => (
              <Container className='px-4 py-2'>
                {product?.type === PRODUCT_TYPE.READY_SITE ? (
                  <ReadySiteCard
                    data={product}
                    className='h-full! md:max-h-[410px]! md:min-h-[390px]!'
                  />
                ) : (
                  <ProductCard
                    data={product}
                    className='mx-auto border-[1px]! border-solid! border-[#E5E8EB]!'
                    isLoading={isInfiniteLoading}
                  />
                )}
              </Container>
            )}
          />
        </Container>

        <Container className='hidden md:block'>
          <VirtuosoGrid
            useWindowScroll
            endReached={handleEndReached}
            overscan={200}
            data={products}
            listClassName='mx-4 grid grid-cols-1 gap-4 md:m-0 md:grid-cols-2 lg:grid-cols-3 pb-12'
            itemContent={(index, product) =>
              product?.type === PRODUCT_TYPE.READY_SITE ? (
                <ReadySiteCard
                  data={product}
                  className='h-full! md:max-h-[410px]! md:min-h-[390px]!'
                />
              ) : (
                <ProductCard
                  className='border-[1px]! border-solid! border-[#E5E8EB]!'
                  data={product}
                  isLoading={isInfiniteLoading}
                  data-cy={DATA_CY_PRODUCT_CARD}
                />
              )
            }
          />
        </Container>

        <InfinityScrollLoading />
      </>
    );
  };

  //TODO: handle error properly
  if (infiniteQueryError) {
    throw infiniteQueryError;
  }

  return (
    <>
      {renderProducts(infiniteProducts)}

      <PaginationButtons
        totalPages={totalPages}
        currentPage={currentPage}
        onPageChange={onPageChange}
        pagePath={pagePath}
      />
    </>
  );
}
